// LQC-2674 - which floating reference rates a currency accepts.
//
// Mirrors the seeded ReferenceRate table
// (LQDatabase/LQScripts/02_PostDbDeployScripts/0008 - INSERT ReferenceRate records.sql),
// which is what ValidateReferenceRate checks against. Only nine currencies have any rows
// at all, and the validator's membership test sits OUTSIDE its ContainsKey guard, so a
// currency absent from this table rejects EVERY reference rate - and a floating leg must
// carry one. That is why SEK has no swap convention despite having a calendar.
//
// Inflation indices (CPI / RPI / HICP) are in the same table but belong to inflation
// swaps, so they are listed apart and kept out of the vanilla IRS picker.

const RATES_BY_CURRENCY: Record<string, string[]> = {
  AUD: [
    "AUD-BBSW-1M",
    "AUD-BBSW-2M",
    "AUD-BBSW-3M",
    "AUD-BBSW-4M",
    "AUD-BBSW-5M",
    "AUD-BBSW-6M",
    "AUD-AONIA",
    "AUD-Deposit-12M",
  ],
  CAD: [
    "CAD-CDOR-1M",
    "CAD-CDOR-3M",
    "CAD-CDOR-6M",
    "CAD-CORRA",
    "CAD-Deposit-12M",
  ],
  CHF: [
    "CHF-LIBOR-1M",
    "CHF-LIBOR-2M",
    "CHF-LIBOR-3M",
    "CHF-LIBOR-6M",
    "CHF-LIBOR-12M",
    "CHF-SARON",
  ],
  EUR: [
    "EUR-EURIBOR-1M",
    "EUR-EURIBOR-3M",
    "EUR-EURIBOR-6M",
    "EUR-EURIBOR-12M",
    "EUR-EuroSTR",
  ],
  GBP: [
    "GBP-LIBOR-1M",
    "GBP-LIBOR-3M",
    "GBP-LIBOR-6M",
    "GBP-LIBOR-12M",
    "GBP-SONIA",
  ],
  JPY: [
    "JPY-LIBOR-1M",
    "JPY-LIBOR-3M",
    "JPY-LIBOR-6M",
    "JPY-LIBOR-12M",
    "JPY-TONA",
  ],
  NZD: [
    "NZD-BKBM-1M",
    "NZD-BKBM-3M",
    "NZD-BKBM-6M",
    "NZD-BKBM-12M",
    "NZD-NZIONA",
  ],
  SGD: [
    "SGD-SOR-1M",
    "SGD-SOR-3M",
    "SGD-SOR-4M",
    "SGD-SOR-5M",
    "SGD-SOR-6M",
    "SGD-SOR-12M",
    "SGD-SONAR",
  ],
  USD: [
    "USD-LIBOR-1M",
    "USD-LIBOR-3M",
    "USD-LIBOR-6M",
    "USD-LIBOR-12M",
    "USD-SOFR",
  ],
};

// Inflation indices, for the day an inflation swap ticket is built. Not offered on a
// vanilla IRS leg.
export const INFLATION_INDICES_BY_CURRENCY: Record<string, string[]> = {
  AUD: ["AUD-CPI"],
  CAD: ["CAD-CPI"],
  EUR: [
    "EUR-HICP",
    "EUR-HICPxT",
    "EUR-DECPI",
    "EUR-FRCPI",
    "EUR-FRCPIxT",
    "EUR-ITCPI",
    "EUR-ITCPIxT",
    "EUR-SPCPI",
    "EUR-EXT-CPI",
  ],
  GBP: ["UK-RPI", "UK-CPI", "UK-RPI_Bilateral"],
  JPY: ["JPY-JPCPI", "JPY-CPI-EXF"],
  USD: ["USD-CPI", "USD-CPI-U"],
};

// LQC-2977 - each rate's ReferenceFrequency, from the same seed. CHF-LIBOR-12M and
// SGD-SOR-12M are stored as "12M", which the frequency list calls 1Y, so they are 1Y here.
const REFERENCE_RATE_FREQUENCY: Record<string, string> = {
  "AUD-AONIA": "1D",
  "AUD-BBSW-1M": "1M",
  "AUD-BBSW-2M": "2M",
  "AUD-BBSW-3M": "3M",
  "AUD-BBSW-4M": "4M",
  "AUD-BBSW-5M": "5M",
  "AUD-BBSW-6M": "6M",
  "AUD-Deposit-12M": "1Y",
  "CAD-CDOR-1M": "1M",
  "CAD-CDOR-3M": "3M",
  "CAD-CDOR-6M": "6M",
  "CAD-CORRA": "1D",
  "CAD-Deposit-12M": "1Y",
  "CHF-LIBOR-12M": "1Y",
  "CHF-LIBOR-1M": "1M",
  "CHF-LIBOR-2M": "2M",
  "CHF-LIBOR-3M": "3M",
  "CHF-LIBOR-6M": "6M",
  "CHF-SARON": "1D",
  "EUR-EURIBOR-12M": "1Y",
  "EUR-EURIBOR-1M": "1M",
  "EUR-EURIBOR-3M": "3M",
  "EUR-EURIBOR-6M": "6M",
  "EUR-EuroSTR": "1D",
  "GBP-LIBOR-12M": "1Y",
  "GBP-LIBOR-1M": "1M",
  "GBP-LIBOR-3M": "3M",
  "GBP-LIBOR-6M": "6M",
  "GBP-SONIA": "1D",
  "JPY-LIBOR-12M": "1Y",
  "JPY-LIBOR-1M": "1M",
  "JPY-LIBOR-3M": "3M",
  "JPY-LIBOR-6M": "6M",
  "JPY-TONA": "1D",
  "NZD-BKBM-12M": "1Y",
  "NZD-BKBM-1M": "1M",
  "NZD-BKBM-3M": "3M",
  "NZD-BKBM-6M": "6M",
  "NZD-NZIONA": "1D",
  "SGD-SONAR": "1D",
  "SGD-SOR-12M": "1Y",
  "SGD-SOR-1M": "1M",
  "SGD-SOR-3M": "3M",
  "SGD-SOR-4M": "4M",
  "SGD-SOR-5M": "5M",
  "SGD-SOR-6M": "6M",
  "USD-LIBOR-12M": "1Y",
  "USD-LIBOR-1M": "1M",
  "USD-LIBOR-3M": "3M",
  "USD-LIBOR-6M": "6M",
  "USD-SOFR": "1D",
};

// An overnight rate compounds into any payment period, so it fits every coupon frequency.
export const OVERNIGHT_RATE_FREQUENCY = "1D";

export function returnReferenceRateFrequency(referenceRate: string): string | undefined {
  return REFERENCE_RATE_FREQUENCY[referenceRate];
}

export function returnReferenceRatesForCurrency(
  currency: string | undefined | null,
): string[] {
  if (!currency) return [];
  return RATES_BY_CURRENCY[currency.trim().toUpperCase()] ?? [];
}

// A ZCS floating leg takes an inflation index, any other floating leg a rate (LQC-2991).
// Spec F17 also filters by coupon frequency: a term rate must match it, an overnight one
// always fits (LQC-2977).
export function returnReferenceRateOptions(
  currency: string | undefined | null,
  isInflationLeg: boolean,
  couponFrequency?: string,
): string[] {
  if (!isInflationLeg) {
    const rates = returnReferenceRatesForCurrency(currency);
    if (!couponFrequency) return rates;
    return rates.filter((rate) => {
      const frequency = REFERENCE_RATE_FREQUENCY[rate];
      return frequency === couponFrequency || frequency === OVERNIGHT_RATE_FREQUENCY;
    });
  }
  if (!currency) return [];
  return INFLATION_INDICES_BY_CURRENCY[currency.trim().toUpperCase()] ?? [];
}

// False means a floating leg in this currency cannot be sent at all, whatever rate is
// chosen, because the validator has nothing to match against.
export function currencyHasReferenceRates(
  currency: string | undefined | null,
): boolean {
  return returnReferenceRatesForCurrency(currency).length > 0;
}

export function isValidReferenceRateForCurrency(
  currency: string | undefined | null,
  referenceRate: string | undefined | null,
): boolean {
  if (!referenceRate) return false;
  return returnReferenceRatesForCurrency(currency).some(
    (rate) => rate.toLowerCase() === referenceRate.trim().toLowerCase(),
  );
}
